import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Image as ImageIcon, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useCurrencyRail } from "@/context/CurrencyRailContext";

export default function Reports() {
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const { activeRail } = useCurrencyRail();

  // Rail → concrete currency for the monthly report. USD/NGN pin the rail so
  // backend filters transactions and stamps the correct symbol. ALL falls back
  // to the user's default currency (backend handles that when param omitted).
  const railParam = activeRail === "USD" || activeRail === "NGN"
    ? { currency: activeRail }
    : {};

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get("/reports/monthly.png", { params: railParam, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRail]);

  const download = async (kind) => {
    const path = kind === "pdf" ? "/reports/monthly.pdf" : "/reports/monthly.png";
    try {
      const res = await api.get(path, { params: railParam, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ledger-monthly-report.${kind}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${kind.toUpperCase()}`);
    } catch (e) {
      toast.error(`Could not download ${kind.toUpperCase()}`);
    }
  };

  const share = async () => {
    if (!navigator.share || !previewUrl) {
      toast.info("Sharing not supported here — download the PNG and share it manually.");
      return;
    }
    try {
      const blob = await (await fetch(previewUrl)).blob();
      const file = new File([blob], "ledger-report.png", { type: "image/png" });
      await navigator.share({ files: [file], title: "My Accountrix monthly report" });
    } catch (e) {
      // user cancelled or unsupported — nothing to surface
      if (e?.name !== "AbortError") {
        console.debug("[Reports] share failed:", e?.message);
      }
    }
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Recap</div>
          <h1 className="font-display text-4xl mt-2">Monthly report</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            A shareable snapshot of your month. Download as PDF or PNG.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="report-download-pdf"
            variant="outline"
            className="rounded-full h-11 px-4"
            onClick={() => download("pdf")}
          >
            <FileText className="w-4 h-4 mr-2" /> Download PDF
          </Button>
          <Button
            data-testid="report-download-png"
            variant="outline"
            className="rounded-full h-11 px-4"
            onClick={() => download("png")}
          >
            <ImageIcon className="w-4 h-4 mr-2" /> Download PNG
          </Button>
          <Button
            data-testid="report-share"
            className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5"
            onClick={share}
          >
            <Sparkles className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Preview</div>
        <h3 className="font-display text-xl mt-1 mb-4">This month at a glance</h3>
        <div data-testid="report-preview" className="rounded-md overflow-hidden border border-[var(--border)] bg-white flex items-center justify-center min-h-[600px]">
          {loading && <div className="text-sm text-[var(--muted-text)]">Rendering...</div>}
          {!loading && previewUrl && (
            <img src={previewUrl} alt="Monthly report preview" className="w-full max-w-3xl" />
          )}
          {!loading && !previewUrl && (
            <div className="text-sm text-[var(--muted-text)]">
              Add a few transactions to generate your first report.
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            data-testid="report-refresh"
            variant="ghost"
            size="sm"
            onClick={loadPreview}
            className="text-[var(--muted-text)]"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Regenerate preview
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
